import React from 'react';
import { Box, Drawer, IconButton, Stack, Tooltip, Typography, useMediaQuery } from '@mui/material';
import PermIdentityOutlinedIcon from '@mui/icons-material/PermIdentityOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import { signOut } from 'aws-amplify/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';
import { CommonModal, FunctionsEnum, LOGOUT_USER } from '@watter/shared-components';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import ListAltIcon from '@mui/icons-material/ListAlt';
import RecentActorsOutlinedIcon from '@mui/icons-material/RecentActorsOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import { revertAll } from '../../store/actions';
import { NavigationConfig } from '../../utils/navigation-constants';
import { SideMenuStyles } from './sidemenu-styles';
import { LogoIcon } from '../icons/logo-icon';
import { client } from '../../service/apolloClient';
import { usePermissions } from '../../custom_hooks/permissions';

interface IProps {
    menuOpen: any;
    menuClose: any;
}
export const SideMenu: React.FC<IProps> = ({ menuOpen, menuClose }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [modalOpen, setModalOpen] = React.useState<boolean>(false);
    const apolloClient = useApolloClient();
    const location = useLocation();
    const currentPath = location.pathname;
    const { canView } = usePermissions();

    const handleLogout = async () => {
        dispatch(showLoader());
        await client.mutate({
            mutation: LOGOUT_USER,
        });
        setTimeout(async () => {
            await signOut()
                .then(async (res: any) => {
                    console.warn(res, '==> res in signout');
                })
                .catch((err: any) => {
                    console.error('err SignOut: ', err);
                    dispatch(hideLoader());
                })
                .finally(() => {
                    dispatch(hideLoader());
                    dispatch(revertAll());
                    sessionStorage.clear();
                    apolloClient.clearStore();
                    navigate(NavigationConfig.Login);
                });
        }, 1000);
    };
    const pathNavigateClick = (value: any) => {
        navigate(value);
    };

    const MenuItems = React.useMemo(
        () => [
            {
                pathValue: NavigationConfig.Home || NavigationConfig.Notifications,
                title: t('dashboard'),
                icon: <DashboardOutlinedIcon />,
                feature: FunctionsEnum.DASHBOARD,
            },
            {
                pathValue: NavigationConfig.ManageInstaller,
                title: t('manage_installer'),
                icon: <PeopleOutlineOutlinedIcon />,
                feature: FunctionsEnum.MANAGE_INSTALLERS,
            },
            {
                pathValue: NavigationConfig.ManageWorkOrder,
                title: t('manage_work_order'),
                icon: <ListAltIcon />,
                feature: FunctionsEnum.MANAGE_WORKORDERS,
            },
            {
                pathValue: NavigationConfig.ManageCustomer,
                title: t('manage_customer'),
                icon: <RecentActorsOutlinedIcon />,
                feature: FunctionsEnum.MANAGE_CUSTOMERS,
            },
            {
                pathValue: NavigationConfig.ManageAdmin,
                title: t('manage_admin'),
                icon: <AdminPanelSettingsOutlinedIcon />,
                feature: FunctionsEnum.MANAGE_ADMINS,
            },
            {
                pathValue: NavigationConfig.ProfileSetting,
                title: t('profile_settings'),
                icon: <PermIdentityOutlinedIcon />,
                feature: FunctionsEnum.PROFILE,
            },
        ],
        [t],
    );

    const navigateToDashboard = () => {
        navigate(NavigationConfig.Home);
    };
    const matches = useMediaQuery(theme => theme.breakpoints.down('lg'));
    const sidemenuContent = () => {
        return (
            <SideMenuStyles height="100%">
                <Box>
                    <Box className="logo" textAlign="center" onClick={() => navigateToDashboard()}>
                        <LogoIcon />
                    </Box>
                    <Stack className="listItems" height="100%" alignItems={{ xs: 'flex-start', lg: 'center' }}>
                        {MenuItems.filter(item => canView(item.feature)).map(items => (
                            <Tooltip
                                title={items.title}
                                arrow
                                placement="right"
                                key={items.pathValue}
                                data-test={items.title}
                            >
                                <Box
                                    key={items?.pathValue}
                                    display={{ xs: 'flex', lg: 'block' }}
                                    mb={{ xs: 2, lg: 0 }}
                                    alignItems="center"
                                    columnGap={1}
                                    onClick={() => pathNavigateClick(items.pathValue)}
                                >
                                    <IconButton
                                        className={
                                            currentPath.startsWith(items.pathValue) ||
                                            (currentPath === NavigationConfig.Notifications &&
                                                items.pathValue === NavigationConfig.Home)
                                                ? 'active'
                                                : ''
                                        }
                                        sx={{ display: 'block' }}
                                    >
                                        {items.icon}
                                    </IconButton>
                                    {matches && (
                                        <Typography
                                            sx={{ cursor: 'pointer' }}
                                            variant="subtitle1"
                                            fontFamily="good_sansmedium"
                                        >
                                            {items.title}
                                        </Typography>
                                    )}
                                </Box>
                            </Tooltip>
                        ))}
                    </Stack>
                </Box>
                <Box className="logoutItem" display="flex" justifyContent="center">
                    <Tooltip title={t('logout')} arrow placement="right">
                        <Box
                            display={{ xs: 'flex', lg: 'block' }}
                            mb={{ xs: 2, lg: 0 }}
                            alignItems="center"
                            columnGap={1}
                            onClick={() => setModalOpen(true)}
                        >
                            <IconButton>
                                <LogoutIcon />
                            </IconButton>
                            {matches && (
                                <Typography sx={{ cursor: 'pointer' }} variant="subtitle1" fontFamily="good_sansmedium">
                                    {t('logout')}
                                </Typography>
                            )}
                        </Box>
                    </Tooltip>
                </Box>
                <CommonModal
                    modalOpen={modalOpen}
                    header={
                        <Stack rowGap={1} pb={1}>
                            <Typography variant="h5" color="secondary" textAlign="center">
                                {t('confirm_logout')}
                            </Typography>
                        </Stack>
                    }
                    cancelBtnName={t('cancel')}
                    AcceptBtnName={t('yes')}
                    closePopup={() => setModalOpen(false)}
                    acceptBtn={handleLogout}
                >
                    {t('logout_text')}
                </CommonModal>
            </SideMenuStyles>
        );
    };
    return matches ? (
        <Drawer open={menuOpen} onClose={menuClose}>
            {sidemenuContent()}
        </Drawer>
    ) : (
        sidemenuContent()
    );
};
